import { Ticker } from 'pixi.js';
import { AssetLoader } from '../asset/AssetLoader';
import { DefaultRandom, type Random } from '../core/Random';
import type { Disposable } from '../core/Event';
import { BingoGameModel } from '../model/bingo/BingoGameModel';
import { PlayerProfile } from '../model/profile/PlayerProfile';
import { DEFAULT_THEME, type Theme } from '../theme';
import { BingoCardGrid } from '../widgets/bingo/BingoCardGrid';
import { columnLetterFor } from '../widgets/bingo/generateBingoCard';
import { NumberCaller } from '../widgets/bingo/NumberCaller';
import type { BingoCard } from '../widgets/bingo/types';
import { BingoHudComponent } from './components/BingoHudComponent';
import { BingoWinSequenceComponent } from './components/BingoWinSequenceComponent';
import { SceneBase } from './SceneBase';
import type { SceneLayoutContext } from './SceneComponent';

const STARTING_BALANCE = 1000;
const BUY_IN = 50;
const CALL_INTERVAL_MS = 2200;
const MAX_CALLS_PER_GAME = 50;

/**
 * Bingo demo. Buy in for {@link BUY_IN} coins → fresh card → numbers
 * auto-called every {@link CALL_INTERVAL_MS} ms → tap to mark cells →
 * any win pays the configured multiplier × buy-in. Game ends on first
 * blackout, on a winning line, or after {@link MAX_CALLS_PER_GAME} calls.
 *
 * <p>Pixi-app lifecycle + strict-mode-unmount handling come from
 * {@link SceneBase}. Game state is on {@link BingoGameModel}; the
 * {@link BingoHudComponent} subscribes directly so balance + status text
 * stay in sync. The scene's job is the master state machine: handle buy-in,
 * tick the call timer, react to {@code bingo-won} / {@code game-ended}.
 */
export class BingoDemoScene extends SceneBase {
  private hud: BingoHudComponent | null = null;
  private winSequence: BingoWinSequenceComponent | null = null;
  private grid: BingoCardGrid | null = null;
  private caller: NumberCaller | null = null;
  private callTickHandler: ((tick: Ticker) => void) | null = null;
  private elapsedSinceCall = 0;
  private readonly random: Random;
  private readonly model: BingoGameModel;
  private modelSubscriptions: Disposable[] = [];
  private profileSubscription: Disposable | null = null;

  constructor(
    container: HTMLElement,
    theme: Theme = DEFAULT_THEME,
    profile: PlayerProfile | null = null,
  ) {
    super(container, { theme });
    this.random = new DefaultRandom();
    this.model = new BingoGameModel({
      startingBalance: profile?.balance ?? STARTING_BALANCE,
      buyIn: BUY_IN,
      maxCallsPerGame: MAX_CALLS_PER_GAME,
    });
    if (profile) {
      this.profileSubscription = this.model.events.on('balance-changed', () => {
        profile.setBalance(this.model.balance);
      });
    }
  }

  protected async preload(): Promise<void> {
    await AssetLoader.shared.loadTheme(this.theme);
  }

  protected buildContent(): void {
    this.winSequence = new BingoWinSequenceComponent(this.app.ticker, {
      fillColor: this.theme.palette.warm,
      strokeColor: this.theme.palette.accent,
    });
    this.app.stage.addChild(this.winSequence);

    this.hud = new BingoHudComponent(this.model, {
      onBuyIn: () => this.handleBuyIn(),
    });
    this.app.stage.addChild(this.hud);

    this.modelSubscriptions.push(
      this.model.events.on('game-started', (event) => {
        this.buildGameWidgets(event.card);
        this.startCallTicker();
        this.layout();
      }),
      this.model.events.on('number-called', (event) => {
        this.caller?.call(event.number);
        this.hud?.setStatusText(
          `call ${event.callsMade}/${event.maxCalls} · ${columnLetterFor(event.number)}-${event.number}`,
        );
      }),
      this.model.events.on('cell-marked', (event) => {
        this.grid?.mark(event.col, event.row);
      }),
      this.model.events.on('bingo-won', (event) => {
        this.grid?.highlightWinCells(event.win.cells);
        this.winSequence?.playWin(event.winnings);
      }),
      this.model.events.on('game-ended', () => {
        this.stopCallTicker();
      }),
    );
  }

  protected onResize(): void {
    this.layout();
  }

  protected disposeResources(wasInitialized: boolean): void {
    this.stopCallTicker();
    this.profileSubscription?.dispose();
    this.profileSubscription = null;
    for (const sub of this.modelSubscriptions) sub.dispose();
    this.modelSubscriptions = [];
    this.model.dispose();
    if (!wasInitialized) return;

    this.hud?.destroy();
    this.hud = null;
    this.winSequence?.destroy();
    this.winSequence = null;
    this.grid?.destroy();
    this.grid = null;
    this.caller?.destroy();
    this.caller = null;
  }

  private layout(): void {
    if (!this.hud || !this.winSequence) return;
    const { width, height } = this.app.renderer;
    const centerX = Math.round(width / 2);

    if (this.caller) {
      this.caller.x = centerX;
      this.caller.y = 130;
    }

    let machineTop = 0;
    let machineBottom = 0;
    if (this.grid) {
      const gridTop = 200;
      this.grid.x = centerX - this.grid.totalWidth() / 2;
      this.grid.y = gridTop;
      machineTop = gridTop;
      machineBottom = gridTop + this.grid.totalHeight();
    }

    const ctx: SceneLayoutContext = {
      width,
      height,
      centerX,
      machineTop,
      machineBottom,
      spinButtonY: machineBottom > 0 ? machineBottom + 80 : Math.round(height / 2 + 110),
    };
    this.hud.layout(ctx);
    this.winSequence.layout(ctx);
  }

  private handleBuyIn(): void {
    if (this.model.gameActive) return;
    // Tear down any leftover widgets from a previous round.
    this.destroyGameWidgets();
    this.model.tryStartGame(this.random);
  }

  private buildGameWidgets(card: BingoCard): void {
    this.grid = new BingoCardGrid(card);
    this.grid.onCellTap = (col, row, number) => {
      this.model.tryMarkCell(col, row, number);
    };
    this.app.stage.addChild(this.grid);

    this.caller = new NumberCaller();
    this.app.stage.addChild(this.caller);
  }

  private destroyGameWidgets(): void {
    if (this.grid) {
      this.grid.destroy();
      this.grid = null;
    }
    if (this.caller) {
      this.caller.destroy();
      this.caller = null;
    }
  }

  private startCallTicker(): void {
    this.elapsedSinceCall = CALL_INTERVAL_MS; // call immediately
    if (this.callTickHandler) return;
    this.callTickHandler = (tick: Ticker) => {
      if (!this.model.gameActive) return;
      this.elapsedSinceCall += tick.deltaMS;
      if (this.elapsedSinceCall >= CALL_INTERVAL_MS) {
        this.elapsedSinceCall = 0;
        this.model.callNext(this.random);
      }
    };
    this.app.ticker.add(this.callTickHandler);
  }

  private stopCallTicker(): void {
    if (!this.callTickHandler) return;
    this.app.ticker.remove(this.callTickHandler);
    this.callTickHandler = null;
  }
}
